import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Save, Loader2, Plus, Trash2, Check, ChevronsUpDown, Download } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useCanManageModels } from "@/hooks/usePermissions";
import {
  createAIModel,
  updateAIModel,
  listAIProviders,
  listAIModelTypes,
} from "@/services/aiModelsService";
import { fetchGoogleModels } from "@/services/aiProvidersService";
import { cn } from "@/lib/utils";
import type {
  AIModel as APIAIModel,
  AIModelType,
} from "@/types/aiModels";
import type { AIProvider } from "@/types/aiProviders";

interface AIModelFormProps {
  onBack: () => void;
  onSave: () => void;
  editingModel: APIAIModel | null;
}

interface PricingTier {
  id?: string;
  tier_name?: string;
  min_tokens: number;
  max_tokens: number | null;
  input_cost_per_1k: number;
  output_cost_per_1k: number;
  is_active?: boolean;
  tier_order?: number;
  model_type_id?: number;
  model_type_name?: string;
}

interface FormData {
  name: string;
  provider_id: number;
  model_identifier: string;
  description: string;
  type_id: number;
  is_active: boolean;
  is_integration_active: boolean;
}

export const AIModelForm = ({
  onBack,
  onSave,
  editingModel,
}: AIModelFormProps) => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    provider_id: 0,
    model_identifier: "",
    description: "",
    type_id: 1,
    is_active: true,
    is_integration_active: false,
  });
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [modelTypes, setModelTypes] = useState<AIModelType[]>([]);
  const [googleModels, setGoogleModels] = useState<any[]>([]);
  const [isFetchingGoogleModels, setIsFetchingGoogleModels] = useState(false);
  const [modelComboboxOpen, setModelComboboxOpen] = useState(false);
  const [modelSearchValue, setModelSearchValue] = useState("");
  const [pricingTiers, setPricingTiers] = useState<PricingTier[]>([
    {
      min_tokens: 0,
      max_tokens: null,
      input_cost_per_1k: 0,
      output_cost_per_1k: 0,
      is_active: true,
      tier_order: 0,
      model_type_id: undefined, // Tipo será definido individualmente, independente do tipo do modelo
    },
  ]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const canManageModels = useCanManageModels();

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (editingModel) {
      setFormData({
        name: editingModel.name,
        provider_id: editingModel.provider_id,
        model_identifier: editingModel.model_identifier,
        description: editingModel.description || "",
        type_id: editingModel.type_id,
        is_active: editingModel.is_active,
        is_integration_active: editingModel.is_integration_active ?? false,
      });
      setPricingTiers(
        editingModel.pricing_tiers?.map((tier) => ({
          id: tier.id,
          tier_name: tier.tier_name,
          min_tokens: tier.min_tokens,
          max_tokens: tier.max_tokens,
          input_cost_per_1k: tier.input_cost_per_1k,
          output_cost_per_1k: tier.output_cost_per_1k,
          is_active: tier.is_active,
          tier_order: tier.tier_order,
          model_type_id: tier.model_type_id,
          model_type_name: tier.model_type_name,
        })) || [
          {
            min_tokens: 0,
            max_tokens: null,
            input_cost_per_1k: 0,
            output_cost_per_1k: 0,
            is_active: true,
            tier_order: 0,
            model_type_id: undefined, // Tipo será definido individualmente pelo usuário, independente do tipo do modelo
          },
        ]
      );
    }
  }, [editingModel]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const [providersRes, typesRes] = await Promise.all([
        listAIProviders(),
        listAIModelTypes(),
      ]);

      if (providersRes.success && providersRes.data) {
        setProviders(providersRes.data);
        const activeProviders = providersRes.data.filter((p) => p.is_active);
        if (!editingModel && activeProviders.length === 1) {
          setFormData((prev) => ({
            ...prev,
            provider_id: activeProviders[0].id,
          }));
        }
      }
      if (typesRes.success && typesRes.data) {
        setModelTypes(typesRes.data);
        if (!editingModel && typesRes.data.length > 0) {
          setFormData((prev) => ({
            ...prev,
            type_id: typesRes.data[0].id,
          }));
          // Inicializar primeira faixa com primeiro tipo disponível (independente do tipo do modelo)
          setPricingTiers([
            {
              min_tokens: 0,
              max_tokens: null,
              input_cost_per_1k: 0,
              output_cost_per_1k: 0,
              is_active: true,
              tier_order: 0,
              model_type_id: typesRes.data.length > 0 ? typesRes.data[0].id : undefined, // Tipo independente do modelo
            },
          ]);
        }
      }
    } catch (error) {
      toast({
        title: "Erro ao carregar dados",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveModel = async () => {
    if (!canManageModels) {
      toast({
        title: "Sem permissão",
        description: "Você não tem permissão para gerenciar modelos",
        variant: "destructive",
      });
      return;
    }

    if (!formData.name.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "O nome do modelo é obrigatório",
        variant: "destructive",
      });
      return;
    }

    if (!formData.model_identifier.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "O identificador do modelo é obrigatório",
        variant: "destructive",
      });
      return;
    }

    // Validar que não contém "models/" e remover se houver
    let cleanIdentifier = formData.model_identifier.trim();
    if (cleanIdentifier.startsWith("models/")) {
      cleanIdentifier = cleanIdentifier.substring(7);
    }
    if (cleanIdentifier.includes("models/")) {
      toast({
        title: "Formato inválido",
        description: "O identificador do modelo não deve conter o prefixo 'models/'. Remova o prefixo.",
        variant: "destructive",
      });
      return;
    }

    if (!formData.provider_id || formData.provider_id === 0) {
      toast({
        title: "Campo obrigatório",
        description: "Selecione um provedor",
        variant: "destructive",
      });
      return;
    }

    if (!formData.type_id || formData.type_id === 0) {
      toast({
        title: "Campo obrigatório",
        description: "Selecione um tipo de modelo",
        variant: "destructive",
      });
      return;
    }

    for (let i = 0; i < pricingTiers.length; i++) {
      const tier = pricingTiers[i];

      if (!tier.model_type_id || tier.model_type_id === 0) {
        toast({
          title: "Erro na faixa de preço",
          description: `Faixa ${i + 1}: Selecione um tipo de modelo`,
          variant: "destructive",
        });
        return;
      }

      if (tier.max_tokens !== null && tier.min_tokens >= tier.max_tokens) {
        toast({
          title: "Erro na faixa de preço",
          description: `Faixa ${i + 1}: Tokens mínimos (${tier.min_tokens}) deve ser menor que tokens máximos (${tier.max_tokens})`,
          variant: "destructive",
        });
        return;
      }

      if (tier.input_cost_per_1k < 0 || tier.output_cost_per_1k < 0) {
        toast({
          title: "Erro na faixa de preço",
          description: `Faixa ${i + 1}: Os custos devem ser valores positivos`,
          variant: "destructive",
        });
        return;
      }

      if (i > 0) {
        const prevTier = pricingTiers[i - 1];
        if (
          prevTier.max_tokens !== null &&
          tier.min_tokens < prevTier.max_tokens
        ) {
          toast({
            title: "Erro na faixa de preço",
            description: `Faixa ${i + 1}: Sobreposição detectada. Tokens mínimos (${tier.min_tokens}) deve ser >= tokens máximos da faixa anterior (${prevTier.max_tokens})`,
            variant: "destructive",
          });
          return;
        }
      }
    }

    setSaving(true);
    try {
      // Garantir que o identificador não tem "models/" antes de salvar
      let cleanIdentifier = formData.model_identifier.trim();
      if (cleanIdentifier.startsWith("models/")) {
        cleanIdentifier = cleanIdentifier.substring(7);
      }

      const modelData = {
        name: formData.name,
        provider_id: formData.provider_id,
        model_identifier: cleanIdentifier,
        description: formData.description,
        type_id: formData.type_id,
        is_active: formData.is_active,
        is_integration_active: formData.is_integration_active,
        pricing_tiers: pricingTiers.map((tier, index) => ({
          ...(tier.id && { id: tier.id }), // Incluir ID se estiver editando
          tier_name: tier.tier_name || `Faixa ${index + 1}`,
          min_tokens: tier.min_tokens,
          max_tokens: tier.max_tokens,
          input_cost_per_1k: tier.input_cost_per_1k,
          output_cost_per_1k: tier.output_cost_per_1k,
          is_active: tier.is_active ?? true,
          tier_order: index,
          model_type_id: tier.model_type_id, // Cada faixa tem seu próprio tipo independente
        })),
      };

      const response = editingModel
        ? await updateAIModel(editingModel.id, modelData)
        : await createAIModel(modelData);

      if (response.success) {
        toast({
          title: editingModel ? "Modelo atualizado" : "Modelo criado",
          description: "Operação realizada com sucesso",
        });
        onSave();
      } else {
        toast({
          title: "Erro ao salvar modelo",
          description: response.error?.message || "Erro desconhecido",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro ao salvar modelo",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const addPricingTier = () => {
    setPricingTiers((prev) => {
      let newMinTokens = 0;
      if (prev.length > 0) {
        const lastTier = prev[prev.length - 1];
        newMinTokens = lastTier.max_tokens || lastTier.min_tokens + 100000;
      }

      // Usar o primeiro tipo disponível, mas é independente do tipo do modelo
      const defaultTypeId = modelTypes.length > 0 ? modelTypes[0].id : undefined;

      return [
        ...prev,
        {
          min_tokens: newMinTokens,
          max_tokens: null,
          input_cost_per_1k: 0,
          output_cost_per_1k: 0,
          is_active: true,
          tier_order: prev.length,
          model_type_id: defaultTypeId, // Tipo independente para cada faixa
        },
      ];
    });
  };

  const updatePricingTier = (
    index: number,
    field: keyof PricingTier,
    value: any
  ) => {
    setPricingTiers((prev) =>
      prev.map((tier, i) => (i === index ? { ...tier, [field]: value } : tier))
    );
  };

  const removePricingTier = (index: number) => {
    setPricingTiers((prev) => prev.filter((_, i) => i !== index));
  };

  const isGoogleProvider = () => {
    const selectedProvider = providers.find(
      (p) => p.id === formData.provider_id
    );
    return selectedProvider?.provider_id === "gemini" ||
           selectedProvider?.name?.toLowerCase().includes("google") ||
           selectedProvider?.name?.toLowerCase().includes("gemini");
  };

  const handleFetchGoogleModels = async () => {
    if (!formData.provider_id || !isGoogleProvider()) {
      toast({
        title: "Erro",
        description: "Selecione um provedor Google/Gemini primeiro",
        variant: "destructive",
      });
      return;
    }

    setIsFetchingGoogleModels(true);
    try {
      const response = await fetchGoogleModels(formData.provider_id);
      if (response.success && response.data) {
        setGoogleModels(response.data.models || []);
        toast({
          title: "Modelos carregados",
          description: `${response.data.models?.length || 0} modelos encontrados`,
        });
      } else {
        toast({
          title: "Erro ao buscar modelos",
          description: response.error?.message || "Erro desconhecido",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro ao buscar modelos",
        description:
          error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setIsFetchingGoogleModels(false);
    }
  };

  const handleSelectGoogleModel = (model: any) => {
    // Remover o prefixo "models/" do name se presente
    let modelIdentifier = model.name;
    if (modelIdentifier.startsWith("models/")) {
      modelIdentifier = modelIdentifier.substring(7); // Remove "models/"
    }

    setFormData((prev) => ({
      ...prev,
      model_identifier: modelIdentifier,
      name: model.displayName || model.name,
      description: model.description || "",
    }));
    setModelComboboxOpen(false);
  };

  const filteredGoogleModels = googleModels.filter((model) =>
    model.name?.toLowerCase().includes(modelSearchValue.toLowerCase()) ||
    model.displayName?.toLowerCase().includes(modelSearchValue.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Carregando dados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      <PageHeader
        title={editingModel ? "Editar Modelo" : "Novo Modelo de IA"}
        description={
          editingModel
            ? "Atualize as informações do modelo"
            : "Adicione um novo modelo de IA ao sistema"
        }
        onBack={onBack}
        backDisabled={saving}
        actions={
          <Button
            onClick={handleSaveModel}
            disabled={saving}
            className="bg-gradient-primary text-white hover:opacity-90"
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Salvar
              </>
            )}
          </Button>
        }
      />

      <main className="container mx-auto p-6">
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informações do Modelo</CardTitle>
              <CardDescription>
                Configure as informações básicas do modelo de IA
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome do Modelo *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        name: e.target.value,
                      }))
                    }
                    placeholder="Ex: GPT-4 Turbo"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="provider">Provedor *</Label>
                  <Select
                    value={formData.provider_id.toString()}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        provider_id: parseInt(value),
                      }))
                    }
                    disabled={
                      providers.length === 0 ||
                      providers.filter((p) => p.is_active).length === 1
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          providers.length === 0
                            ? "Carregando provedores..."
                            : "Selecione um provedor"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {providers.length === 0 ? (
                        <SelectItem value="0" disabled>
                          Nenhum provedor disponível
                        </SelectItem>
                      ) : (
                        providers
                          .filter((provider) => provider.is_active)
                          .map((provider) => (
                            <SelectItem
                              key={provider.id}
                              value={provider.id.toString()}
                            >
                              {provider.name}
                            </SelectItem>
                          ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="modelType">Tipo de Modelo *</Label>
                  <Select
                    value={formData.type_id.toString()}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        type_id: parseInt(value),
                      }))
                    }
                    disabled={modelTypes.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          modelTypes.length === 0
                            ? "Carregando tipos..."
                            : "Selecione um tipo"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {modelTypes.length === 0 ? (
                        <SelectItem value="0" disabled>
                          Nenhum tipo disponível
                        </SelectItem>
                      ) : (
                        modelTypes.map((type) => (
                          <SelectItem
                            key={type.id}
                            value={type.id.toString()}
                          >
                            {type.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  {isGoogleProvider() && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleFetchGoogleModels}
                        disabled={isFetchingGoogleModels || !formData.provider_id}
                        className="flex-1"
                      >
                        {isFetchingGoogleModels ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Buscando...
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4 mr-2" />
                            Buscar Modelos
                          </>
                        )}
                      </Button>
                      {googleModels.length > 0 && (
                        <Popover
                          open={modelComboboxOpen}
                          onOpenChange={setModelComboboxOpen}
                        >
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={modelComboboxOpen}
                              className="flex-1 justify-between"
                            >
                              {formData.model_identifier
                                ? googleModels.find(
                                    (m) => {
                                      const modelId = m.name.startsWith("models/")
                                        ? m.name.substring(7)
                                        : m.name;
                                      return modelId === formData.model_identifier;
                                    }
                                  )?.displayName || formData.model_identifier
                                : "Selecione um modelo..."}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
                            <Command shouldFilter={false}>
                              <CommandInput
                                placeholder="Buscar modelo..."
                                value={modelSearchValue}
                                onChange={(e) => setModelSearchValue(e.target.value)}
                              />
                              <CommandList>
                                {filteredGoogleModels.length === 0 ? (
                                  <CommandEmpty>Nenhum modelo encontrado.</CommandEmpty>
                                ) : (
                                  <CommandGroup>
                                    {filteredGoogleModels.map((model) => {
                                      const modelId = model.name.startsWith("models/")
                                        ? model.name.substring(7)
                                        : model.name;
                                      const isSelected = formData.model_identifier === modelId;
                                      return (
                                        <CommandItem
                                          key={model.name}
                                          value={`${model.name} ${model.displayName || ""} ${model.description || ""}`}
                                          onSelect={() => handleSelectGoogleModel(model)}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              isSelected ? "opacity-100" : "opacity-0"
                                            )}
                                          />
                                          <div className="flex flex-col">
                                            <span className="font-medium">
                                              {model.displayName || model.name}
                                            </span>
                                            {model.description && (
                                              <span className="text-xs text-muted-foreground">
                                                {model.description}
                                              </span>
                                            )}
                                          </div>
                                        </CommandItem>
                                      );
                                    })}
                                  </CommandGroup>
                                )}
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      )}
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="modelIdentifier">
                      Identificador do Modelo *
                    </Label>
                    <Input
                      id="modelIdentifier"
                      value={formData.model_identifier}
                      onChange={(e) => {
                        let value = e.target.value;
                        // Remover "models/" se o usuário tentar digitar
                        if (value.startsWith("models/")) {
                          value = value.substring(7);
                        }
                        // Aplicar regex: permite apenas letras minúsculas, números, hífens, pontos e underscores
                        // Converte automaticamente para minúsculas
                        const regex = /^[a-z0-9._-]*$/;
                        const cleanedValue = value.toLowerCase();

                        // Verificar se todos os caracteres são válidos
                        if (regex.test(cleanedValue)) {
                          setFormData((prev) => ({
                            ...prev,
                            model_identifier: cleanedValue,
                          }));
                        } else {
                          // Se contém caracteres inválidos, filtrar apenas os válidos
                          const filteredValue = cleanedValue.split('').filter(char =>
                            /[a-z0-9._-]/.test(char)
                          ).join('');
                          setFormData((prev) => ({
                            ...prev,
                            model_identifier: filteredValue,
                          }));
                        }
                      }}
                      placeholder="Ex: gemini-2.5-pro (apenas letras minúsculas, números, hífens, pontos e underscores)"
                      className={
                        formData.model_identifier?.includes("models/") ||
                        (formData.model_identifier && !/^[a-z0-9._-]+$/.test(formData.model_identifier))
                          ? "border-destructive"
                          : ""
                      }
                    />
                    <p className="text-xs text-muted-foreground">
                      Digite apenas o identificador do modelo sem o prefixo "models/".
                      Apenas letras minúsculas, números, hífens (-), pontos (.) e underscores (_) são permitidos.
                      <br />
                      Exemplo: "gemini-2.5-pro" ou "embedding-gecko-001"
                    </p>
                    {formData.model_identifier?.includes("models/") && (
                      <p className="text-xs text-destructive">
                        ⚠️ O identificador não deve conter "models/". Remova o prefixo.
                      </p>
                    )}
                    {formData.model_identifier && !/^[a-z0-9._-]+$/.test(formData.model_identifier) && (
                      <p className="text-xs text-destructive">
                        ⚠️ O identificador contém caracteres inválidos. Use apenas letras minúsculas, números, hífens, pontos e underscores.
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-2 flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="active"
                    checked={formData.is_active}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_active: e.target.checked,
                      }))
                    }
                  />
                  <Label htmlFor="active">Modelo Ativo</Label>
                </div>
                <div className="space-y-2 flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="integration-active"
                    checked={formData.is_integration_active}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        is_integration_active: e.target.checked,
                      }))
                    }
                  />
                  <Label htmlFor="integration-active">Integração Ativa</Label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrição</Label>
                <Input
                  id="description"
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  placeholder="Descrição do modelo e suas capacidades"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Configuração de Preços</CardTitle>
                  <CardDescription>
                    Configure faixas de preço progressivas. Cada faixa deve ter
                    tokens mínimos menor que tokens máximos e seu próprio tipo de modelo independente. Use "Ilimitado"
                    para última faixa.
                  </CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={addPricingTier}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Faixa
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {pricingTiers.map((tier, index) => (
                <Card key={index} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-medium text-muted-foreground">
                      Faixa {index + 1}
                    </span>
                  </div>
                  <div className="grid grid-cols-6 gap-4 items-end">
                    <div className="space-y-2">
                      <Label>Tipo de Modelo *</Label>
                      <Select
                        value={
                          tier.model_type_id
                            ? tier.model_type_id.toString()
                            : ""
                        }
                        onValueChange={(value) =>
                          updatePricingTier(
                            index,
                            "model_type_id",
                            parseInt(value)
                          )
                        }
                        disabled={modelTypes.length === 0}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              modelTypes.length === 0
                                ? "Carregando tipos..."
                                : "Selecione um tipo"
                            }
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {modelTypes.length === 0 ? (
                            <SelectItem value="0" disabled>
                              Nenhum tipo disponível
                            </SelectItem>
                          ) : (
                            modelTypes.map((type) => (
                              <SelectItem
                                key={type.id}
                                value={type.id.toString()}
                              >
                                {type.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Tokens Mínimos</Label>
                      <Input
                        type="number"
                        min="0"
                        value={tier.min_tokens}
                        onChange={(e) =>
                          updatePricingTier(
                            index,
                            "min_tokens",
                            parseInt(e.target.value) || 0
                          )
                        }
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Tokens Máximos</Label>
                      <Input
                        type="number"
                        min={tier.min_tokens + 1}
                        value={tier.max_tokens || ""}
                        onChange={(e) =>
                          updatePricingTier(
                            index,
                            "max_tokens",
                            e.target.value ? parseInt(e.target.value) : null
                          )
                        }
                        placeholder="Ilimitado"
                      />
                      {tier.max_tokens !== null &&
                        tier.min_tokens >= tier.max_tokens && (
                          <p className="text-xs text-destructive">
                            Deve ser maior que {tier.min_tokens}
                          </p>
                        )}
                    </div>
                    <div className="space-y-2">
                      <Label>Custo Entrada (1k)</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        min="0"
                        value={tier.input_cost_per_1k}
                        onChange={(e) =>
                          updatePricingTier(
                            index,
                            "input_cost_per_1k",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        placeholder="0.00"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Custo Saída (1k)</Label>
                      <Input
                        type="number"
                        step="0.0001"
                        min="0"
                        value={tier.output_cost_per_1k}
                        onChange={(e) =>
                          updatePricingTier(
                            index,
                            "output_cost_per_1k",
                            parseFloat(e.target.value) || 0
                          )
                        }
                        placeholder="0.00"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => removePricingTier(index)}
                      disabled={pricingTiers.length === 1}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </Card>
              ))}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

